import { Controller, useForm } from "react-hook-form";
import {
  alpha,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  MenuItem,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useContext, useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import logo from "../../utils/images/ridePlus.png";
import AuthContext from "../../context/AuthProvider";
import { navigatePostAuth } from "../../utils/postAuthNavigation";
import ProfilePicture from "../profile/profile-picture";
import TenantSelection from "../tenants/tenant-selection";
import CustomPinInput from "../../components/customPinInput";
import { phoneRegex } from "../../utils/validationPattern";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import OtpResendButton from "../../components/otpResendButton";
import { queryClient } from "../../App";
import useTenantBranding from "../../hooks/useTenantBranding";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthTextField from "../../components/auth/AuthTextField";
import PrimaryButton from "../../components/auth/PrimaryButton";
import SecondaryButton from "../../components/auth/SecondaryButton";

function Register({ consentId, headerBranding }) {
  // USESTATE & USEREF
  const [openDrawer, setOpenDrawer] = useState(false);
  const [phone, setPhone] = useState("");
  const [file, setFile] = useState("");
  const [open, setOpen] = useState(false);
  const [requestOtp, setRequestOtp] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState("phone"); // "phone" or "email"
  const [tosAndPPUrl, setTosAndPPUrl] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [pendingOtpAfterTenantSelect, setPendingOtpAfterTenantSelect] =
    useState(false);

  const userData = useRef({});

  // Cooldown hook for OTP resend functionality
  const cooldownHook = useOtpResendCooldown("register", phone);

  const {
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    setAuth,
    setAuthWithTenant,
  } = useContext(AuthContext);
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const tenantId = currentTenant?.id || getTenantIdFromUrl();
  // NAVIGATE & SNACKBAR
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { enqueueSnackbar } = useSnackbar();
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const consentIdFromUrl =
    consentId || searchParams.get("consent_id") || searchParams.get("consentId");
  const clientIdFromUrl =
    searchParams.get("client_id") ||
    searchParams.get("clientId") ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId") ||
    location.state?.client_id ||
    location.state?.clientId;
  const tenantIdFromUrl =
    searchParams.get("tenant_id") ||
    searchParams.get("tenantId") ||
    redirectSearchParams.get("tenant_id") ||
    redirectSearchParams.get("tenantId") ||
    location.state?.tenant_id ||
    location.state?.tenantId;
  const effectiveTenantId = tenantId || tenantIdFromUrl;
  const hasClientAndTenant = !!clientIdFromUrl && !!effectiveTenantId;
  const isConsentFlow =
    !!consentIdFromUrl ||
    searchParams.get("prompt") === "consent" ||
    searchParams.get("prompt") === "register";

  const consentBrandLogo =
    headerBranding?.logoUrl ||
    (isConsentFlow
      ? searchParams.get("tenant_logo") ||
        searchParams.get("tenantLogo") ||
        currentTenant?.logo
      : null);
  const consentBrandName =
    headerBranding?.name ||
    (isConsentFlow
      ? searchParams.get("tenant_name") ||
        searchParams.get("tenantName") ||
        currentTenant?.name
      : null);
  const showConsentTenantHeader =
    isConsentFlow && (consentBrandLogo || consentBrandName);

  // REACT HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
  });

  // MUTATION & USEQUERY
  const { mutate, isLoading } = useMutation(
    (data) => {
      return axios.post("/register", data, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
    },
    {
      onSuccess: (response) => {
        const registrationData = response?.data?.data;

        if (registrationData) {
          // Extract auth tokens from registration response
          const authData = {
            access_token: registrationData.access_token,
            id_token: registrationData.id_token,
            refresh_token: registrationData.refresh_token,
            token_type: registrationData.token_type,
            expires_in: registrationData.expires_in,
          };

          // Set auth context with tokens and tenant
          if (tenantId) {
            setAuthWithTenant(authData, tenantId);
          } else {
            setAuth(authData);
          }

          enqueueSnackbar(`You're successfully registered!`, {
            variant: "success",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });

          navigatePostAuth(navigate, { redirectUri, location });
        }
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
      },
    }
  );
  const { isFetching: otpIsFetching } = useQuery(
    [
      "otp_on_registration",
      userData.current?.phone,
      tenantId,
      clientIdFromUrl,
    ],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "signup",
          phone: userData.current.phone,
          tenant_id: tenantId,
          ...(clientIdFromUrl && { client_id: clientIdFromUrl }),
        },
      }),
    {
      enabled: requestOtp,
      onSuccess: () => {
        enqueueSnackbar(`Code sent to your phone!`, {
          variant: "success",
          preventDuplicate: true,
          autoHideDuration: 2000,
        });
        cooldownHook.startCooldown();
        setRequestOtp(false);
        queryClient.clear();
      },
      onError: (err) => {
        enqueueSnackbar(
          err?.response?.data?.error?.message ||
          err?.message ||
          "Network Error!",
          {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
        // Keep the user on the registration form when OTP request fails.
        setOpenDrawer(false);
        setRequestOtp(false);
      },
    }
  );
  const {
    isFetching: tosLinksIsFetching,
    isLoading: tosLinksIsLoading,
    isFetched: tosLinksIsFetched,
  } = useQuery(
    ["tos-pp-url", consentIdFromUrl, clientIdFromUrl, isConsentFlow],
    async () => {
      if (!hasClientAndTenant) return null;
      return axios.get("/clients/tos-pp-url", {
        params: {
          client_id: clientIdFromUrl,
          tenant_id: effectiveTenantId,
        },
      });
    },
    {
      enabled: hasClientAndTenant,
      retry: false,
      onSuccess: (response) => {
        if (!response) {
          setTosAndPPUrl(null);
          return;
        }
        const data = response?.data?.data || response?.data;
        const privacyPolicyUrl = data?.privacy_policy_url || "";
        const termsOfServiceUrl = data?.terms_of_service_url || "";
        if (privacyPolicyUrl || termsOfServiceUrl) {
          setTosAndPPUrl({
            privacy_policy_url: privacyPolicyUrl,
            terms_of_service_url: termsOfServiceUrl,
          });
          return;
        }
        setTosAndPPUrl(null);
      },
      onError: () => {
        // Intentionally hide the checkbox when no TOS/PP data can be fetched.
        setTosAndPPUrl(null);
      },
    }
  );

  // EVENT HANDLERS & HELPERS
  const handleClick = () => {
    if (redirectUri) {
      navigate(redirectUri, { replace: true });
    } else {
      navigate(`/login${location.search || ""}`, {
        replace: true,
        state: location.state,
      });
    }
  };
  const handleRegister = (form) => {
    if (hasClientAndTenant && (tosLinksIsFetching || tosLinksIsLoading)) {
      enqueueSnackbar("Please wait while loading Privacy Policy and Terms.", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 2500,
      });
      return;
    }

    if (tosAndPPUrl && !agreeToTerms) {
      enqueueSnackbar(
        "Please agree to the Privacy Policy and Terms to continue.",
        {
          variant: "warning",
          preventDuplicate: true,
          autoHideDuration: 2500,
        }
      );
      return;
    }

    // If no tenant_id in URL, show tenant selection
    if (!tenantId) {
      setUserIdentifier(form?.phone || form?.email);
      setIdentifierType(form?.phone ? "phone" : "email");
      setShowTenantSelection(true);
      userData.current = form; // Store form data for later use
      if (hasClientAndTenant) setPendingOtpAfterTenantSelect(true);
      return;
    }

    // Otherwise proceed with normal OTP request
    setRequestOtp(true);
    setOpenDrawer(true);
    setPhone(form?.phone);
    userData.current = form;
  };
  const handleSignup = (form) => {
    userData.current = { ...userData.current, otp: form.otp };
    const dataToBeSent =
      file !== ""
        ? { ...userData.current, profile_picture: file }
        : userData.current;
    const payload = tenantId
      ? { ...dataToBeSent, tenant_id: tenantId }
      : dataToBeSent;
    mutate(payload);
  };

  // USEEFFECT
  // Ensure we don't auto-redirect with an existing session from another tenant
  useEffect(() => {
    setAuth({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle tenant selection
  const handleTenantSelected = () => {
    // After tenant selection, proceed with OTP request
    setShowTenantSelection(false);
    setPhone(userData.current?.phone);

    if (isConsentFlow) {
      // Ensure the user checks the checkbox (if tos/pp exists) before continuing.
      setAgreeToTerms(false);
      setPendingOtpAfterTenantSelect(true);
    } else {
      setRequestOtp(true);
      setOpenDrawer(true);
    }
  };

  useEffect(() => {
    if (!pendingOtpAfterTenantSelect) return;
    if (!isConsentFlow) return;
    // Wait until tos/pp fetch completes (success or error) so we know whether
    // we need a checkbox or can continue immediately.
    if (!tosLinksIsFetched) return;

    if (tosAndPPUrl && !agreeToTerms) return;

    setPendingOtpAfterTenantSelect(false);
    setRequestOtp(true);
    setOpenDrawer(true);
  }, [
    pendingOtpAfterTenantSelect,
    isConsentFlow,
    tosLinksIsFetched,
    tosAndPPUrl,
    agreeToTerms,
  ]);

  const handleBackFromTenantSelection = () => {
    setShowTenantSelection(false);
    setUserIdentifier("");
    userData.current = {};
  };

  // Branding avatar shown in the OTP-verify header (mirrors staging's logic).
  const verifyHeaderAvatar =
    showConsentTenantHeader && consentBrandLogo ? (
      <Avatar
        src={consentBrandLogo}
        alt=""
        variant="rounded"
        imgProps={{ style: { objectFit: "contain" } }}
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          mb: 2,
          borderRadius: 2.5,
        }}
      />
    ) : showConsentTenantHeader && consentBrandName ? (
      <Avatar
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          mb: 2,
          borderRadius: 2.5,
          fontSize: { xs: "1.5rem", sm: "2rem" },
          fontWeight: 700,
        }}
      >
        {String(consentBrandName).trim().charAt(0) || "?"}
      </Avatar>
    ) : (tenantLogo || !hideDefaultBranding) ? (
      <Avatar
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          mb: 2,
          borderRadius: 2.5,
        }}
        src={tenantLogo || logo}
      />
    ) : null;

  // Consent brand mark shown atop the registration form.
  const formHeaderBrand =
    showConsentTenantHeader && consentBrandLogo ? (
      <Box
        component="img"
        src={consentBrandLogo}
        alt=""
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          objectFit: "contain",
          mb: 1.5,
          borderRadius: 2.5,
        }}
      />
    ) : showConsentTenantHeader && consentBrandName && !consentBrandLogo ? (
      <Avatar
        sx={{
          width: { xs: 60, sm: 80 },
          height: { xs: 60, sm: 80 },
          mb: 1.5,
          borderRadius: 2.5,
          fontSize: { xs: "1.5rem", sm: "2rem" },
          fontWeight: 700,
        }}
      >
        {String(consentBrandName).trim().charAt(0) || "?"}
      </Avatar>
    ) : null;

  // RENDER
  if (showTenantSelection) {
    return (
      <TenantSelection
        userIdentifier={userIdentifier}
        identifierType={identifierType}
        onBack={handleBackFromTenantSelection}
        onTenantSelected={handleTenantSelected}
      />
    );
  }

  // OTP verification step
  if (openDrawer && !otpIsFetching) {
    return (
      <AuthLayout
        title="Verify Your Phone"
        onSubmit={(e) => {
          e.preventDefault();
          if (isLoading || inputValue?.length !== 6) return;
          handleSignup({ otp: inputValue });
        }}
        header={
          verifyHeaderAvatar && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {verifyHeaderAvatar}
            </Box>
          )
        }
        footer={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: 1.5,
            }}
          >
            <SecondaryButton onClick={() => setOpenDrawer(false)}>
              Back
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              loading={isLoading}
              disabled={inputValue?.length !== 6}
            >
              Continue
            </PrimaryButton>
          </Box>
        }
      >
        <Typography
          sx={{
            fontSize: { xs: 14, sm: 15 },
            mb: 3,
            textAlign: "center",
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          Please enter the code sent to{" "}
          <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
            {phone}
          </Box>{" "}
          via SMS
        </Typography>
        <Box sx={{ mb: 3 }}>
          <CustomPinInput
            handleOnSubmit={handleSignup}
            setInputValue={setInputValue}
          />
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "primary.lighter"
                : alpha(theme.palette.primary.main, 0.1),
            border: "1px solid",
            borderColor: "primary.main",
          }}
        >
          <OtpResendButton
            cooldownHook={cooldownHook}
            onResend={() => setRequestOtp(true)}
            isLoading={otpIsFetching}
            size="small"
            showMessage={true}
          />
        </Box>
      </AuthLayout>
    );
  }

  // Registration form
  return (
    <AuthLayout
      title="Create Your Account"
      subtitle="Join us and get started with your journey"
      onSubmit={handleSubmit(handleRegister)}
      header={
        formHeaderBrand && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {formHeaderBrand}
          </Box>
        )
      }
      footer={
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1.5,
          }}
        >
          <SecondaryButton onClick={() => handleClick()}>Back</SecondaryButton>
          <PrimaryButton
            type="submit"
            loading={otpIsFetching}
            disabled={isConsentFlow && tosLinksIsFetching}
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              boxShadow: (t) =>
                `0 3px 5px 2px ${alpha(t.palette.primary.main, 0.3)}`,
              "&:hover": {
                bgcolor: "primary.dark",
                boxShadow: (t) =>
                  `0 4px 8px 2px ${alpha(t.palette.primary.main, 0.4)}`,
              },
            }}
          >
            Register
          </PrimaryButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <ProfilePicture
          edit={true}
          isOpen={open}
          setIsOpen={setOpen}
          imageSrc={file}
          setFile={setFile}
        />
      </Box>
      <Controller
        name="first_name"
        control={control}
        rules={{ required: "First Name is required" }}
        render={({ field }) => (
          <AuthTextField
            label="First Name"
            autoComplete="given-name"
            defaultValue={userData.current?.first_name}
            error={errors?.first_name}
            helperText={errors?.first_name ? errors.first_name.message : null}
            {...field}
          />
        )}
      />
      <Controller
        name="middle_name"
        control={control}
        rules={{ required: "Middle Name is required" }}
        render={({ field }) => (
          <AuthTextField
            label="Middle Name"
            autoComplete="additional-name"
            defaultValue={userData.current?.middle_name}
            error={errors?.middle_name}
            helperText={errors?.middle_name ? errors.middle_name.message : null}
            {...field}
          />
        )}
      />
      <Controller
        name="last_name"
        control={control}
        rules={{ required: "Last Name is required" }}
        render={({ field }) => (
          <AuthTextField
            label="Last Name"
            autoComplete="family-name"
            defaultValue={userData.current?.last_name}
            error={errors?.last_name}
            helperText={errors?.last_name ? errors.last_name.message : null}
            {...field}
          />
        )}
      />
      <Controller
        name="gender"
        control={control}
        render={({ field }) => (
          <AuthTextField
            label="Gender"
            select
            autoComplete="sex"
            defaultValue={userData.current?.gender || ""}
            error={errors?.gender}
            helperText={errors?.gender ? errors.gender.message : null}
            {...field}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="PREFER_NOT_TO_SAY">Prefer Not To Say</MenuItem>
          </AuthTextField>
        )}
      />
      <Controller
        name="phone"
        control={control}
        rules={{
          required: "Phone is required",
          pattern: { value: phoneRegex, message: "Invalid input" },
        }}
        render={({ field }) => (
          <AuthTextField
            variant="phone"
            label="Phone Number"
            placeholder="Enter your phone number"
            defaultValue={userData.current?.phone}
            error={errors?.phone}
            helperText={errors?.phone ? errors.phone.message : null}
            {...field}
          />
        )}
      />
      {tosAndPPUrl && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                sx={{ p: 0.5, mt: 0.25 }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I agree to the{" "}
                {tosAndPPUrl?.privacy_policy_url && (
                  <Link
                    href={tosAndPPUrl.privacy_policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                )}
                {tosAndPPUrl?.privacy_policy_url &&
                tosAndPPUrl?.terms_of_service_url
                  ? " and "
                  : ""}
                {tosAndPPUrl?.terms_of_service_url && (
                  <Link
                    href={tosAndPPUrl.terms_of_service_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>
                )}
                .
              </Typography>
            }
            sx={{ alignItems: "center", m: 0, gap: 1 }}
          />
        </Box>
      )}
    </AuthLayout>
  );
}
export default Register;
